import type { DragState, GameInput, Rect, UiAction } from "../game/input";
import type { MoveAttempt, Position, PowerupKind, RotationDir } from "../game/types";

/**
 * GameInput implementation for the opponent's mirror GameController.
 * Events are fed in from the network layer via push*() methods, then
 * consumed by the controller's update() loop through the GameInput API.
 */
export class MirrorInputAdapter implements GameInput {
  private readonly MAX_QUEUE_ITEMS = 256;
  private swapQueue: MoveAttempt[] = [];
  private actionQueue: UiAction[] = [];
  private clickQueue: Position[] = [];
  private rotationQueue: RotationDir[] = [];

  private trimQueue<T>(queue: T[]): void {
    const overflow = queue.length - this.MAX_QUEUE_ITEMS;
    if (overflow > 0) {
      queue.splice(0, overflow);
    }
  }

  // ── Push methods (called by network event handler) ──────────────────────

  pushSwap(from: Position, to: Position): void {
    this.swapQueue.push({ from, to });
    this.trimQueue(this.swapQueue);
  }

  pushAction(action: UiAction): void {
    this.actionQueue.push(action);
    this.trimQueue(this.actionQueue);
  }

  pushBoardClick(pos: Position): void {
    this.clickQueue.push(pos);
    this.trimQueue(this.clickQueue);
  }

  pushRotation(dir: RotationDir): void {
    this.rotationQueue.push(dir);
    this.trimQueue(this.rotationQueue);
  }

  /** Discard any buffered events (e.g. on disconnect/reset). */
  flush(): void {
    this.swapQueue.length = 0;
    this.actionQueue.length = 0;
    this.clickQueue.length = 0;
    this.rotationQueue.length = 0;
  }

  getQueueSize(): number {
    return this.swapQueue.length + this.actionQueue.length + this.clickQueue.length + this.rotationQueue.length;
  }

  // ── GameInput interface ─────────────────────────────────────────────────

  consumeSwap(): MoveAttempt | null {
    return this.swapQueue.shift() ?? null;
  }

  consumeActions(): UiAction[] {
    const out = [...this.actionQueue];
    this.actionQueue.length = 0;
    return out;
  }

  consumeBoardClick(): Position | null {
    return this.clickQueue.shift() ?? null;
  }

  getDragState(): DragState | null {
    return null;
  }

  getHoverCell(): Position | null {
    return null;
  }

  getHoveredPowerup(): PowerupKind | null {
    return null;
  }

  getHoveredRect(): Rect | null {
    return null;
  }

  consumeMusicVolumeChange(): number | null {
    return null;
  }

  consumeSfxVolumeChange(): number | null {
    return null;
  }

  consumeRotationSelection(): RotationDir | null {
    return this.rotationQueue.shift() ?? null;
  }

  consumeFriendBattleIndex(): number | null {
    return null;
  }

  consumeFriendAcceptIndex(): number | null {
    return null;
  }

  consumeFriendDeclineIndex(): number | null {
    return null;
  }

  consumeFriendRemoveIndex(): number | null {
    return null;
  }

  consumeShopBuyIndex(): number | null {
    return null;
  }

  consumeShopEquipIndex(): number | null {
    return null;
  }

  consumeShopBuyGemsIndex(): number | null {
    return null;
  }

  consumeProfileEquipIndex(): number | null {
    return null;
  }

  consumeAvatarSelectNftIndex(): number | null {
    return null;
  }

  consumeTokenShopItemIndex(): number | null {
    return null;
  }

  consumeTappedLobbyIndex(): number | null {
    return null;
  }

  consumeTappedInboxIndex(): number {
    return -1;
  }

  getRadialMenuState(): null {
    return null;
  }

  consumeRadialRelease(): { kind: "powerup" | "trap"; pointerX: number; pointerY: number; hubX: number; hubY: number } | null {
    return null;
  }

  hasPendingRadialRelease(): boolean {
    return false;
  }
}
